import { useImageAssetsStore } from '@/stores/imageAssets';
import { wireJsonBag, wireString } from '@/utils/wire';
import type { Device } from '@/types';

/**
 * The device's own thumbnail, resolved through the S3-key blob cache first.
 *
 * Widgets that read `device.thumbnailUrl` directly lost the picture whenever the
 * row came back cloud-compact (the hub sends `null` there) or with a presigned
 * URL the browser cannot reach (a PATCH answers from the hub's own MinIO). Both
 * are the same row, so this is the one door to read it through.
 */
export function getDefaultThumbnailUrl(device: Device): string | null {
  const imageAssetsStore = useImageAssetsStore();
  const url = device.thumbnailUrl ?? null;
  const s3Key = device.thumbnailS3Key;

  if (s3Key) {
    const cached = imageAssetsStore.getImageUrlByS3Key(s3Key);
    // Trigger lazy fetch when not yet in cache; the reactive assets ref
    // will re-evaluate any computed/template that reads this.
    if (!cached) void imageAssetsStore.fetchImageUrlByS3Key(s3Key);
    return cached ?? url;
  }

  return url;
}

/**
 * The S3 key of the picture a binary sensor shows while it reads active — the
 * single `settings.stateThumbnailS3Key` slot the editor writes. An empty answer
 * means the device has none and keeps its default thumbnail.
 */
export function stateThumbnailS3Key(device: Device): string | null {
  return wireString(wireJsonBag(device.settings).stateThumbnailS3Key) || null;
}

/**
 * The active-state picture, resolved through the S3-key blob cache exactly like
 * the default thumbnail (a cloud-compact row carries only the key). Null means
 * there is no state image.
 */
export function getStateThumbnailUrl(device: Device): string | null {
  const imageAssetsStore = useImageAssetsStore();
  const url = device.stateThumbnailUrl ?? null;
  const s3Key = stateThumbnailS3Key(device);

  if (s3Key) {
    const cached = imageAssetsStore.getImageUrlByS3Key(s3Key);
    if (!cached) void imageAssetsStore.fetchImageUrlByS3Key(s3Key);
    return cached ?? url;
  }

  return url;
}
